/**
 * Tracks-panel row ordering (ADR-041 D-5; REQ-UI-170 amended 2026-10-08;
 * REQ-UI-110 pattern).
 *
 * Mirrors :file:`detections/detection-sort.ts`: a comparator per persisted
 * sort field over the client's :type:`ObjectTrackRecord`, the producer name
 * joined through :type:`ProducerNameIndex`, nullable fields partitioned
 * nulls-last by the shared :func:`sortBy`.
 *
 * # A row moves only for a difference the operator can see
 *
 * Unlike the Detections list, which only ever *inserts* at its top, this
 * list's rows carry mutable keys: every detection advances its track's head
 * time, count and duration. Sorted by the exact head time, a provider whose
 * tracks all report at a few hertz rotates the entire list on every commit —
 * each track leaps to the top as its detection lands and sinks as the others
 * follow — and the keep-in-view hold (REQ-UI-182) then chases the selected row
 * through the list several times a second: the scrollbar cycled, rows passed
 * under the pointer so the chevron could not be clicked, and hover highlights
 * flashed (operator report 2026-10-08, ``provider-surge``). Two rules fix it:
 *
 * 1. **Sort keys are compared at the resolution the row displays.** Head time
 *    to the second (``HH:MM:SS``) and duration to the second (``m:ss``); count
 *    and name are what the row shows already. Two rows the operator cannot
 *    tell apart never swap.
 * 2. **Ties keep the order the operator already sees.** The caller passes the
 *    previously rendered order; rows with equal keys stay in that relative
 *    order, and a track not in it (newly presented) sorts first among its
 *    ties, newest track first. Under a steady provider the default order
 *    therefore settles to the order the tracks first appeared and stays
 *    there; a track that goes quiet sinks once the others cross into a newer
 *    second, and a new track enters at the top.
 *
 * The tie-break is the *incoming* order of :func:`sortBy`, which that helper
 * keeps regardless of direction — so an ``asc`` sort flips the key, never the
 * tie-break, and a sort-field change keeps every tie where it was.
 */
import type { TrackListSort } from "@/auth/session-types";
import type { ProducerNameIndex } from "@/lib/use-producer-names";
import {
  type ComparatorMap,
  type SortSelection,
  compareNumbers,
  compareStringsCI,
  sortBy,
} from "@/lib/list-sort";
import {
  type ObjectTrackRecord,
  firstTimeMs,
  headTimeMs,
} from "@/object-tracks/object-track-store";
import {
  trackDurationSeconds,
  trackMemberCount,
  trackPrimaryClass,
} from "@/object-tracks/track-display";

/** The panel's built-in order: newest head first. */
export const TRACK_DEFAULT_SORT: SortSelection<TrackListSort["field"]> = {
  field: "head_time",
  direction: "desc",
};

/**
 * The previously rendered order — ``track_id`` → index — that ties resolve
 * to. :func:`trackOrderIndex` builds one from a sorted list.
 */
export type TrackOrderIndex = ReadonlyMap<string, number>;

/** Index a rendered order for the next :func:`sortTrackRows` call. */
export function trackOrderIndex(sorted: readonly ObjectTrackRecord[]): TrackOrderIndex {
  return new Map(sorted.map((track, index) => [track.track_id, index]));
}

/** The head time as the row shows it: whole seconds (``HH:MM:SS``). */
export function displayedHeadSeconds(track: ObjectTrackRecord): number {
  return Math.floor(headTimeMs(track) / 1000);
}

/** The duration as the row shows it: whole seconds (``m:ss``); 0 when unknown. */
export function displayedDurationSeconds(track: ObjectTrackRecord): number {
  return Math.floor(trackDurationSeconds(track) ?? 0);
}

function compareIds(a: ObjectTrackRecord, b: ObjectTrackRecord): number {
  return a.track_id < b.track_id ? -1 : a.track_id > b.track_id ? 1 : 0;
}

/**
 * The rows in the order ties resolve to: tracks absent from ``previous``
 * first (newest track first, then by id, so two new tracks are deterministic),
 * then the rest in their previous relative order. With no previous order
 * every track is "new" and the whole list falls to newest-track-first.
 */
function tieOrder(
  tracks: readonly ObjectTrackRecord[],
  previous: TrackOrderIndex | undefined,
): ObjectTrackRecord[] {
  return [...tracks].sort((a, b) => {
    const ia = previous?.get(a.track_id);
    const ib = previous?.get(b.track_id);
    if (ia === undefined && ib === undefined) {
      return compareNumbers(firstTimeMs(b), firstTimeMs(a)) || compareIds(a, b);
    }
    if (ia === undefined) {
      return -1;
    }
    if (ib === undefined) {
      return 1;
    }
    return ia - ib;
  });
}

export function sortTrackRows(
  tracks: readonly ObjectTrackRecord[],
  names: ProducerNameIndex,
  selection: SortSelection<TrackListSort["field"]>,
  previous?: TrackOrderIndex,
): ObjectTrackRecord[] {
  const comparators: ComparatorMap<ObjectTrackRecord, TrackListSort["field"]> = {
    head_time: (a, b) => compareNumbers(displayedHeadSeconds(a), displayedHeadSeconds(b)),
    duration: (a, b) => compareNumbers(displayedDurationSeconds(a), displayedDurationSeconds(b)),
    count: (a, b) => compareNumbers(trackMemberCount(a), trackMemberCount(b)),
    classification: (a, b) =>
      compareStringsCI(trackPrimaryClass(a) ?? "", trackPrimaryClass(b) ?? ""),
    platform_name: (a, b) =>
      compareStringsCI(names.nameFor(a.producer_id), names.nameFor(b.producer_id)),
  };
  // classification is the one nullable field → nulls-last when active. The
  // sort menu stopped offering it on 2026-10-01 (REQ-UI-170 amended); the
  // comparator stays so a Profile that saved it before then still sorts.
  const isAbsent =
    selection.field === "classification"
      ? (t: ObjectTrackRecord): boolean => trackPrimaryClass(t) === null
      : undefined;
  return sortBy(tieOrder(tracks, previous), selection, comparators, isAbsent);
}
